import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { apiClient } from "../api/client";
import Card from "../components/Card";

interface NotificationResult {
  message: string;
  count?: number;
  recipient?: string;
  daysRemaining?: number;
}

function NotificationManagement() {
  const { user } = useAuth();
  const [periodId, setPeriodId] = useState("");
  const [evaluationId, setEvaluationId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<NotificationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sendNotification = async (
    type: "PERIOD_STARTED" | "REMINDER" | "COMPLETED",
    data: { periodId?: string; evaluationId?: string }
  ) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiClient.post<NotificationResult>("/notifications/send", {
        type,
        ...data,
      });

      setResult(response);
    } catch (err: any) {
      setError(err.message || "Failed to send notification");
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodStarted = () => {
    if (!periodId.trim()) {
      setError("Please enter a period ID");
      return;
    }
    sendNotification("PERIOD_STARTED", { periodId: periodId.trim() });
  };

  const handleReminder = () => {
    if (!periodId.trim()) {
      setError("Please enter a period ID");
      return;
    }
    sendNotification("REMINDER", { periodId: periodId.trim() });
  };

  const handleCompleted = () => {
    if (!evaluationId.trim()) {
      setError("Please enter an evaluation ID");
      return;
    }
    sendNotification("COMPLETED", { evaluationId: evaluationId.trim() });
  };

  if (user?.role !== "ADMIN" && user?.role !== "MANAGER") {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        Access denied. Only ADMIN and MANAGER roles can manage notifications.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Notification Management</h2>
        <p className="text-sm text-slate-500 mt-1">
          Send email notifications for evaluation periods and reminders
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {result && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          <p className="font-semibold">{result.message}</p>
          {result.count !== undefined && <p className="text-sm mt-1">Recipients: {result.count}</p>}
          {result.recipient && <p className="text-sm mt-1">Sent to: {result.recipient}</p>}
          {result.daysRemaining !== undefined && (
            <p className="text-sm mt-1">Days remaining: {result.daysRemaining}</p>
          )}
        </div>
      )}

      <Card
        title="📣 Period Started Notification"
        subtitle="Notify all users when a new evaluation period begins"
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Period ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={periodId}
              onChange={(e) => setPeriodId(e.target.value)}
              placeholder="e.g., period-2025-q1"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={handlePeriodStarted}
            disabled={loading}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
          >
            {loading ? "Sending..." : "📧 Send Period Started Notifications"}
          </button>
          <p className="text-xs text-slate-500">
            This will send an email to all active users informing them about the new evaluation
            period.
          </p>
        </div>
      </Card>

      <Card
        title="⏰ Evaluation Reminder"
        subtitle="Send reminders to users with incomplete evaluations"
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Period ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={periodId}
              onChange={(e) => setPeriodId(e.target.value)}
              placeholder="e.g., period-2025-q1"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={handleReminder}
            disabled={loading}
            className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
          >
            {loading ? "Sending..." : "📬 Send Reminders"}
          </button>
          <p className="text-xs text-slate-500">
            This will send reminder emails only to users who have incomplete (DRAFT) evaluations for
            the specified period.
          </p>
        </div>
      </Card>

      <Card
        title="✅ Completion Notification"
        subtitle="Send confirmation when an evaluation is completed"
      >
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Evaluation ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={evaluationId}
              onChange={(e) => setEvaluationId(e.target.value)}
              placeholder="e.g., eval-abc123"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={handleCompleted}
            disabled={loading}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {loading ? "Sending..." : "✉️ Send Completion Notification"}
          </button>
          <p className="text-xs text-slate-500">
            This will send a confirmation email to the user who completed the evaluation.
          </p>
        </div>
      </Card>

      <Card title="💡 Email Provider Setup" subtitle="Configure email service">
        <div className="text-sm space-y-2 text-slate-700">
          <p>The system supports multiple email providers:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>
              <strong>Resend</strong>: Set{" "}
              <code className="bg-slate-100 px-2 py-0.5 rounded">RESEND_API_KEY</code> (100
              emails/day free)
            </li>
            <li>
              <strong>SendGrid</strong>: Set{" "}
              <code className="bg-slate-100 px-2 py-0.5 rounded">SENDGRID_API_KEY</code> (100
              emails/day free)
            </li>
            <li>
              <strong>Mock</strong>: No API key required (logs to console only)
            </li>
          </ul>
          <p className="mt-3">
            Set <code className="bg-slate-100 px-2 py-0.5 rounded">EMAIL_FROM</code> to customize
            the sender address (default: noreply@pd-evaluation.app)
          </p>
        </div>
      </Card>
    </div>
  );
}

export default NotificationManagement;
